// Follows "HR Design rules.md": GEN-07
import React from 'react';
import {
  Check,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleX,
  Clock,
  FileBadge,
  History,
  LoaderCircle,
  Lock,
  Send,
  UserX,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';

export type StatusTone = 'warning' | 'success' | 'error' | 'info' | 'muted';

/**
 * The subtle badge colours of each tone (DESIGN_SYSTEM.md › HR status mapping), for a `Badge` that needs a
 * tone without the chip. `info` and `muted` are both neutral; their icon tells them apart.
 */
export const TONE_STYLE: Record<StatusTone, string> = {
  warning: 'border-warning-border bg-warning-subtle text-warning-subtle-foreground',
  success: 'border-success-border bg-success-subtle text-success-subtle-foreground',
  error: 'border-destructive-border bg-destructive-subtle text-destructive-subtle-foreground',
  info: 'border-info-border bg-info-subtle text-info-subtle-foreground',
  muted: 'border-info-border bg-info-subtle text-info-subtle-foreground',
};

const TONE_VARIANT = {
  warning: 'warning-subtle',
  success: 'success-subtle',
  error: 'destructive-subtle',
  info: 'info-subtle',
  muted: 'info-subtle',
} as const;

/** Status colour is never the only cue (GEN-07): Pending a clock, Approved a check, Rejected an alert. */
const TONE_ICON: Record<StatusTone, LucideIcon> = {
  warning: Clock,
  success: CircleCheck,
  error: CircleAlert,
  info: CircleDot,
  muted: CircleDashed,
};

/**
 * The usual tone of a request status (HR Module Prototype `STATUS_BADGE`): pending warns, approved and
 * done succeed, rejected, denied, a US-spelled "canceled", excluded and locked are errors; a UK-spelled
 * "cancelled", withdrawn and archived are neutral.
 */
const STATUS_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  requested: 'warning',
  inprogress: 'info',
  peerssent: 'info',
  approved: 'success',
  completed: 'success',
  submitted: 'success',
  generated: 'success',
  delivered: 'success',
  rejected: 'error',
  declined: 'error',
  denied: 'error',
  canceled: 'error',
  excluded: 'error',
  locked: 'error',
  withdrawn: 'muted',
  cancelled: 'muted',
  archived: 'muted',
  notsent: 'muted',
};

/** Statuses with an icon of their own in the prototype; the rest show their tone's icon. */
const STATUS_ICON: Record<string, LucideIcon> = {
  inprogress: LoaderCircle,
  generated: FileBadge,
  delivered: Check,
  denied: CircleX,
  canceled: CircleX,
  excluded: UserX,
  archived: History,
  locked: Lock,
  peerssent: Send,
  notsent: CircleDashed,
};

const normalizeStatus = (status: string | undefined) =>
  (status ?? '').replace(/[\s_-]/g, '').toLowerCase();

export const statusTone = (status: string | undefined): StatusTone =>
  STATUS_TONE[normalizeStatus(status)] ?? 'muted';

interface StatusChipProps {
  /** The tone; by default the usual one of `status` (see `statusTone`). */
  tone?: StatusTone;
  status?: string;
  /**
   * Show the tone's icon. By default a status tone (warning, success, error) always has one, and a neutral
   * tone has one when it comes from a `status`; a neutral label such as a category shows none. Pass an
   * icon to show that one instead of the tone's (the prototype's Expired chip uses a clock alert).
   */
  icon?: boolean | LucideIcon;
  children: React.ReactNode;
  className?: string;
}

/**
 * A status as a subtle badge with its icon and label. Long text is cut to the chip's width (the app-wide
 * `OverflowTooltip` shows it in full).
 */
export const StatusChip = ({ tone, status, icon, children, className }: Readonly<StatusChipProps>) => {
  const resolved = tone ?? statusTone(status);
  const showIcon = icon ?? (resolved !== 'info' && resolved !== 'muted' ? true : status !== undefined);
  const Icon =
    typeof icon === 'boolean' || icon === undefined
      ? (STATUS_ICON[normalizeStatus(status)] ?? TONE_ICON[resolved])
      : icon;
  return (
    <Badge variant={TONE_VARIANT[resolved]} className={cn('max-w-full', className)}>
      {showIcon && <Icon aria-hidden="true" />}
      <span className="truncate">{children}</span>
    </Badge>
  );
};
